import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { format, addDays, subDays } from "date-fns";
import { Calendar as CalendarIcon, Building2, Save, ChevronLeft, ChevronRight, MapPin, RefreshCw, Search, X, Users, Stethoscope, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { useDebounce } from "use-debounce";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TallyCard from "@/components/TallyCard";
import MiscEntries from "@/components/MiscEntries";
import ProcedureEntries from "@/components/ProcedureEntries";
import HourlyEntries from "@/components/HourlyEntries";
import TotalsBar from "@/components/TotalsBar";
import CptReferencePanel from "@/components/CptReferencePanel";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import { CPT_CODES as DEFAULT_CPT_CODES, calculateTotals, formatNumber, formatCurrency } from "@/lib/constants";
import { createShift, updateShift, fetchShiftByDate, addToQuickAddCptList } from "@/lib/api";
import { searchCptCodes } from "@/lib/cptLookup";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// CPT Search Context Menu Component
function CptSearchContextMenu({ x, y, cpt, onClose, onAddToMisc, onAddToProcedure, onAddToQuickAdd }) {
  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div 
      className="fixed z-[200] bg-slate-800 border border-slate-600 rounded-lg shadow-xl py-1 min-w-[220px]"
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b border-slate-700">
        <p className="text-xs font-mono text-amber-400 font-bold">{cpt.code}</p>
        <p className="text-xs text-slate-400 truncate max-w-[200px]">{cpt.name}</p>
        <p className="text-xs text-emerald-400 font-mono mt-1">{cpt.wrvu} wRVU</p>
      </div>
      <button
        onClick={() => { onAddToMisc(cpt); onClose(); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-purple-500/20 hover:text-purple-300 transition-colors text-left"
      >
        <Users size={14} className="text-purple-400" />
        Add to Other Patient Visits
      </button>
      <button
        onClick={() => { onAddToProcedure(cpt); onClose(); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-teal-500/20 hover:text-teal-300 transition-colors text-left"
      >
        <Stethoscope size={14} className="text-teal-400" />
        Add to Procedures & Services
      </button>
      <button
        onClick={() => { onAddToQuickAdd(cpt); onClose(); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-blue-500/20 hover:text-blue-300 transition-colors text-left border-t border-slate-700"
      >
        <ListPlus size={14} className="text-blue-400" />
        Add to Quick-Add CPT List
      </button>
    </div>
  );
}

// Helper function to get auth token
const getAuthToken = () => {
  const authData = localStorage.getItem("wrvu_auth") || sessionStorage.getItem("wrvu_auth");
  return authData ? JSON.parse(authData).token : null;
};

let idCounter = 0;
function newId(prefix) {
  idCounter += 1;
  return `${prefix}-${Date.now()}-${idCounter}`;
}

// Create default tallies from custom visit types
const createDefaultTallies = (visitTypes) => {
  return Object.fromEntries(visitTypes.map(c => [c.code, 0]));
};

// Create wRVU map from custom visit types
const createWrvuMap = (visitTypes) => {
  return Object.fromEntries(visitTypes.map(c => [c.code, c.wrvu]));
};

export default function Dashboard({ onSaved, editDate, onEditDateConsumed, retentionRate = 0.76, taxRate = 24, wrvuRate = 25, trackerDate, onTrackerDateChange }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Use trackerDate from props if provided, otherwise use local state
  const [localDate, setLocalDate] = useState(new Date());
  const date = trackerDate || localDate;
  const setDate = onTrackerDateChange || setLocalDate;
  const [clinicLocation, setClinicLocation] = useState("");
  
  // Quick-Add Visit Types (customizable)
  const [quickAddVisits, setQuickAddVisits] = useState(DEFAULT_CPT_CODES);
  const [loadingVisits, setLoadingVisits] = useState(true);
  
  const [tallies, setTallies] = useState(() => createDefaultTallies(DEFAULT_CPT_CODES));
  const [miscEntries, setMiscEntries] = useState([]);
  const [procedureEntries, setProcedureEntries] = useState([]);
  const [hourlyEntries, setHourlyEntries] = useState([]);
  const [activeShiftId, setActiveShiftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loadingDate, setLoadingDate] = useState(false);
  
  // Clinic locations dropdown
  const [savedLocations, setSavedLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationInputRef = useRef(null);
  
  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  // CPT Search state
  const [cptSearchQuery, setCptSearchQuery] = useState("");
  const [debouncedCptQuery] = useDebounce(cptSearchQuery, 200);
  const [cptSearchResults, setCptSearchResults] = useState([]);
  const [showCptSearchResults, setShowCptSearchResults] = useState(false);
  const cptSearchRef = useRef(null);
  
  // CPT Search Context Menu state
  const [searchContextMenu, setSearchContextMenu] = useState(null);
  
  // CPT Panel refresh trigger
  const [cptPanelRefresh, setCptPanelRefresh] = useState(0);

  // Calculate totals using current visit types only
  const wrvuMap = createWrvuMap(quickAddVisits);
  const currentCodes = new Set(quickAddVisits.map(v => v.code));
  
  // Only count tallies for current Quick-Add visit codes (ignore stale codes from old configurations)
  const tallyPatients = Object.entries(tallies)
    .filter(([code]) => currentCodes.has(code))
    .reduce((sum, [, count]) => sum + count, 0);
  const miscPatients = miscEntries.length;
  const totalPatients = tallyPatients + miscPatients;
  
  // Only calculate wRVUs for current Quick-Add visit codes
  const tallyWrvus = Object.entries(tallies)
    .filter(([code]) => currentCodes.has(code))
    .reduce((sum, [code, count]) => sum + count * (wrvuMap[code] || 0), 0);
  const miscWrvus = miscEntries.reduce((sum, e) => sum + (parseFloat(e.wrvus) || 0), 0);
  const procedureWrvus = procedureEntries.reduce((sum, e) => sum + (parseFloat(e.wrvus) || 0), 0);
  const totalWrvus = Math.round((tallyWrvus + miscWrvus + procedureWrvus) * 100) / 100;
  const hourlyPay = hourlyEntries.reduce((sum, e) => sum + (parseFloat(e.rate) || 0) * (parseFloat(e.hours) || 0), 0);
  const wrvuPay = totalWrvus * wrvuRate;
  const preTax = wrvuPay + hourlyPay;
  const postTax = preTax * retentionRate;

  // Track the latest load to prevent race conditions
  const loadIdRef = useRef(0);
  const skipAutoSaveRef = useRef(true); // Skip auto-save during initial load and date switches
  const autoSaveTimerRef = useRef(null);

  // Load Quick-Add Visit Types
  const loadQuickAddVisits = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setLoadingVisits(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/quick-add-visits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuickAddVisits(data);
        // DON'T reset tallies here - let loadShiftForDate handle that
        // This preserves any existing tally data during component remounts
      }
    } catch {
      // Silent fail, use defaults
    } finally {
      setLoadingVisits(false);
    }
  }, []);

  // Load saved clinic locations
  const loadSavedLocations = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/clinic-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedLocations(data);
      }
    } catch {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    loadSavedLocations();
    loadQuickAddVisits();
  }, [loadSavedLocations, loadQuickAddVisits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSelect = (locationName) => {
    setClinicLocation(locationName);
    setShowLocationDropdown(false);
  };

  const loadShiftForDate = useCallback(async (selectedDate, visitTypes) => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const thisLoadId = ++loadIdRef.current;
    skipAutoSaveRef.current = true;
    setLoadingDate(true);
    try {
      const existing = await fetchShiftByDate(dateStr);
      if (thisLoadId !== loadIdRef.current) return;
      if (existing) {
        // Create default tallies based on the provided visit types
        const defaultTallies = createDefaultTallies(visitTypes);
        const loadedTallies = { ...defaultTallies };
        if (existing.tallies) {
          Object.keys(existing.tallies).forEach(key => {
            // Load all tallies from shift (even if not in current quick-add visits)
            loadedTallies[key] = existing.tallies[key];
          });
        }
        setTallies(loadedTallies);
        setMiscEntries(existing.misc_entries || []);
        setProcedureEntries(existing.procedure_entries || []);
        setHourlyEntries(existing.hourly_entries || []);
        setClinicLocation(existing.clinic_location || "");
        setActiveShiftId(existing.id);
        toast.info("Loaded existing shift for this date");
      } else {
        setTallies(createDefaultTallies(visitTypes));
        setMiscEntries([]);
        setProcedureEntries([]);
        setHourlyEntries([]);
        setClinicLocation("");
        setActiveShiftId(null);
      }
    } catch {
      if (thisLoadId !== loadIdRef.current) return;
      setTallies(createDefaultTallies(visitTypes));
      setMiscEntries([]);
      setProcedureEntries([]);
      setHourlyEntries([]);
      setClinicLocation("");
      setActiveShiftId(null);
    } finally {
      if (thisLoadId === loadIdRef.current) {
        setLoadingDate(false);
        // Allow auto-save after load settles
        setTimeout(() => { skipAutoSaveRef.current = false; }, 500);
      }
    }
  }, []);

  // Track if initial load has been done for this mount
  const initialLoadDoneRef = useRef(false);

  // On mount or when quick-add visits finish loading: load shift data for current date
  useEffect(() => {
    // Only load shift once quickAddVisits are ready AND we haven't done initial load yet
    if (!loadingVisits && !initialLoadDoneRef.current && !editDate) {
      initialLoadDoneRef.current = true;
      loadShiftForDate(date, quickAddVisits);
    }
  }, [loadingVisits, date, quickAddVisits, editDate, loadShiftForDate]);

  // Handle editDate from CalendarView — takes priority over mount load
  useEffect(() => {
    if (editDate && !loadingVisits) {
      const parts = editDate.split('-');
      const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      setDate(d);
      loadShiftForDate(d, quickAddVisits);
      if (onEditDateConsumed) onEditDateConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDate, loadingVisits]);

  const handleDateChange = (d) => {
    if (d) {
      setDate(d);
      setCalendarOpen(false);
      loadShiftForDate(d, quickAddVisits);
    }
  };

  const handleIncrement = useCallback((code) => {
    setTallies(prev => ({ ...prev, [code]: prev[code] + 1 }));
  }, []);

  const handleDecrement = useCallback((code) => {
    setTallies(prev => ({ ...prev, [code]: Math.max(0, prev[code] - 1) }));
  }, []);

  const handleSetCount = useCallback((code, value) => {
    setTallies(prev => ({ ...prev, [code]: Math.max(0, value) }));
  }, []);

  // Misc handlers
  const handleAddMisc = () => setMiscEntries(prev => [...prev, { id: newId("misc"), label: "", wrvus: 0 }]);
  const handleRemoveMisc = (id) => setMiscEntries(prev => prev.filter(e => e.id !== id));
  const handleUpdateMisc = (id, field, value) => {
    setMiscEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      if (field === "wrvus") return { ...e, wrvus: value === "" ? 0 : parseFloat(value) || 0 };
      return { ...e, [field]: value };
    }));
  };

  // Procedure handlers
  const handleAddProcedure = () => setProcedureEntries(prev => [...prev, { id: newId("proc"), label: "", wrvus: 0 }]);
  const handleRemoveProcedure = (id) => setProcedureEntries(prev => prev.filter(e => e.id !== id));
  const handleUpdateProcedure = (id, field, value) => {
    setProcedureEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      if (field === "wrvus") return { ...e, wrvus: value === "" ? 0 : parseFloat(value) || 0 };
      return { ...e, [field]: value };
    }));
  };

  // CPT Reference Panel handlers - add from right-click menu
  const handleAddCptToMisc = useCallback((cptEntry) => {
    const newEntry = {
      id: newId("misc"),
      label: `${cptEntry.code} — ${cptEntry.description}`,
      wrvus: cptEntry.wrvu || 0
    };
    setMiscEntries(prev => [...prev, newEntry]);
    toast.success(`Added ${cptEntry.code} to Other Patient Visits`);
  }, []);

  const handleAddCptToProcedure = useCallback((cptEntry) => {
    const newEntry = {
      id: newId("proc"),
      label: `${cptEntry.code} — ${cptEntry.description}`,
      wrvus: cptEntry.wrvu || 0
    };
    setProcedureEntries(prev => [...prev, newEntry]);
    toast.success(`Added ${cptEntry.code} to Procedures & Services`);
  }, []);

  // Hourly handlers
  const handleAddHourly = () => setHourlyEntries(prev => [...prev, { id: newId("hr"), label: "", rate: 0, hours: 0 }]);
  const handleRemoveHourly = (id) => setHourlyEntries(prev => prev.filter(e => e.id !== id));
  const handleUpdateHourly = (id, field, value) => {
    setHourlyEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      if (field === "rate" || field === "hours") {
        return { ...e, [field]: value === "" ? 0 : parseFloat(value) || 0 };
      }
      return { ...e, [field]: value };
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Only save tallies for current Quick-Add visit codes (remove stale codes)
    const cleanTallies = {};
    quickAddVisits.forEach(v => {
      cleanTallies[v.code] = tallies[v.code] || 0;
    });
    
    try {
      const payload = {
        date: format(date, "yyyy-MM-dd"),
        clinic_location: clinicLocation.trim(),
        tallies: cleanTallies,
        misc_entries: miscEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 })),
        procedure_entries: procedureEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 })),
        hourly_entries: hourlyEntries.map(e => ({ id: e.id, label: e.label, rate: parseFloat(e.rate) || 0, hours: parseFloat(e.hours) || 0 })),
      };
      if (activeShiftId) {
        // Only update data fields — don't change the shift's date
        const updatePayload = {
          clinic_location: clinicLocation.trim(),
          tallies: cleanTallies,
          misc_entries: miscEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 })),
          procedure_entries: procedureEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 })),
          hourly_entries: hourlyEntries.map(e => ({ id: e.id, label: e.label, rate: parseFloat(e.rate) || 0, hours: parseFloat(e.hours) || 0 })),
        };
        await updateShift(activeShiftId, updatePayload);
        toast.success("Shift updated");
      } else {
        try {
          const result = await createShift(payload);
          setActiveShiftId(result.id);
          toast.success("Shift saved");
        } catch (err) {
          if (err.response?.status === 409) {
            toast.info("Shift exists for this date — updating it");
            await loadShiftForDate(date, quickAddVisits);
            return;
          }
          throw err;
        }
      }
      if (clinicLocation.trim()) localStorage.setItem("lastClinicLocation", clinicLocation.trim());
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Failed to save shift");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleNewShift = () => {
    skipAutoSaveRef.current = true;
    setTallies(createDefaultTallies(quickAddVisits));
    setMiscEntries([]);
    setProcedureEntries([]);
    setHourlyEntries([]);
    setActiveShiftId(null);
    setDate(new Date());
    toast.info("Started new shift");
  };

  // Refresh handler - reload current date's data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadQuickAddVisits();
      await loadShiftForDate(date, quickAddVisits);
      toast.success("Data refreshed");
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // CPT Search handler - just updates query, actual search runs on debounced value
  const handleCptSearch = (query) => {
    setCptSearchQuery(query);
    if (query.trim().length < 2) {
      setCptSearchResults([]);
      setShowCptSearchResults(false);
    }
  };

  // Run search on debounced query (avoids iterating 11k codes on every keypress)
  useEffect(() => {
    if (debouncedCptQuery.trim().length >= 2) {
      const results = searchCptCodes(debouncedCptQuery, 20);
      setCptSearchResults(results);
      setShowCptSearchResults(results.length > 0);
    }
  }, [debouncedCptQuery]);

  // Add CPT from search to Procedures & Services
  const handleAddCptToProcedureFromSearch = (cpt) => {
    const newEntry = {
      id: newId("proc"),
      label: `${cpt.code} - ${cpt.name}`,
      wrvus: String(cpt.wrvu),
    };
    setProcedureEntries(prev => [...prev, newEntry]);
    setCptSearchQuery("");
    setCptSearchResults([]);
    setShowCptSearchResults(false);
    setSearchContextMenu(null);
    toast.success(`Added ${cpt.code} to Procedures & Services`);
  };

  // Add CPT from search to Other Patient Visits (Misc)
  const handleAddCptToMiscFromSearch = (cpt) => {
    const newEntry = {
      id: newId("misc"),
      label: `${cpt.code} - ${cpt.name}`,
      wrvus: String(cpt.wrvu),
    };
    setMiscEntries(prev => [...prev, newEntry]);
    setCptSearchQuery("");
    setCptSearchResults([]);
    setShowCptSearchResults(false);
    setSearchContextMenu(null);
    toast.success(`Added ${cpt.code} to Other Patient Visits`);
  };

  // Add CPT from search to Quick-Add CPT List (side panel)
  const handleAddCptToQuickAddFromSearch = async (cpt) => {
    setCptSearchQuery("");
    setCptSearchResults([]);
    setShowCptSearchResults(false);
    setSearchContextMenu(null);
    
    try {
      await addToQuickAddCptList(cpt.code, cpt.name, cpt.wrvu);
      toast.success(`Added ${cpt.code} to Quick-Add CPT List`);
      // Trigger refresh of the CPT panel
      setCptPanelRefresh(prev => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to Quick-Add CPT List");
    }
  };

  // Handle right-click on search result
  const handleSearchResultContextMenu = (e, cpt) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchContextMenu({ x: e.clientX, y: e.clientY, cpt });
  };

  // Close CPT search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cptSearchRef.current && !cptSearchRef.current.contains(e.target)) {
        setShowCptSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-save: debounced save on any data change
  useEffect(() => {
    if (skipAutoSaveRef.current || loadingDate) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      // Only save tallies for current Quick-Add visit codes (remove stale codes)
      const cleanTallies = {};
      quickAddVisits.forEach(v => {
        cleanTallies[v.code] = tallies[v.code] || 0;
      });
      
      const miscPayload = miscEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 }));
      const procedurePayload = procedureEntries.map(e => ({ id: e.id, label: e.label, wrvus: parseFloat(e.wrvus) || 0 }));
      const hourlyPayload = hourlyEntries.map(e => ({ id: e.id, label: e.label, rate: parseFloat(e.rate) || 0, hours: parseFloat(e.hours) || 0 }));

      try {
        if (activeShiftId) {
          await updateShift(activeShiftId, {
            clinic_location: clinicLocation.trim(),
            tallies: cleanTallies,
            misc_entries: miscPayload,
            procedure_entries: procedurePayload,
            hourly_entries: hourlyPayload,
          });
        } else {
          const result = await createShift({
            date: format(date, "yyyy-MM-dd"),
            clinic_location: clinicLocation.trim(),
            tallies: cleanTallies,
            misc_entries: miscPayload,
            procedure_entries: procedurePayload,
            hourly_entries: hourlyPayload,
          });
          setActiveShiftId(result.id);
        }
        if (onSaved) onSaved();
        setAutoSaved(true);
        setTimeout(() => setAutoSaved(false), 2000);
      } catch {
        // Silent fail for auto-save — manual save still available
      }
    }, 500);

    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tallies, miscEntries, procedureEntries, hourlyEntries, clinicLocation, quickAddVisits]);

  useEffect(() => {
    if (clinicLocation.trim()) localStorage.setItem("lastClinicLocation", clinicLocation.trim());
  }, [clinicLocation]);

  return (
    <div className="pb-56">
      {/* CPT Reference Panel - Left sidebar */}
      <CptReferencePanel 
        onAddToMisc={handleAddCptToMisc}
        onAddToProcedure={handleAddCptToProcedure}
        refreshTrigger={cptPanelRefresh}
      />

      {/* Sticky Header with Auto-save indicator */}
      <div className={`sticky top-14 z-30 backdrop-blur-sm -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-5 border-b ${
        isDark 
          ? 'bg-[#0f172a]/95 border-slate-700/30' 
          : 'bg-gray-50/95 border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold tracking-tight ${
              isDark ? 'text-slate-100' : 'text-gray-900'
            }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              Shift Tracker
            </h1>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
              Track your Patient Visits, wRVU's, & Hourly Pay
            </p>
          </div>
          <div className="flex items-center gap-3">
            {autoSaved && (
              <span className="text-emerald-400 font-semibold text-sm animate-pulse">• Auto-saved</span>
            )}
            <ThemeToggle />
            <Button
              data-testid="refresh-tracker-btn"
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center gap-2 ${
                isDark 
                  ? 'text-slate-400 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 hover:text-slate-200'
                  : 'text-gray-600 border-gray-300 hover:bg-white hover:border-gray-400 hover:text-gray-900'
              }`}
              title="Refresh data"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-5 mb-8">
        <div className="flex-1">
          <label className={`text-sm uppercase tracking-widest font-semibold mb-2 block ${
            isDark ? 'text-slate-500' : 'text-gray-600'
          }`}>Date</label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleDateChange(subDays(date, 1))}
              className={`h-14 w-14 border flex-shrink-0 ${
                isDark 
                  ? 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 text-slate-400 hover:text-slate-200'
                  : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-600 hover:text-gray-900'
              }`}
              title="Previous day"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  data-testid="date-picker-trigger"
                  variant="outline"
                  className={`flex-1 justify-start text-left font-normal border h-14 text-lg px-5 ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 text-slate-200'
                      : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-900'
                  }`}
                >
                  <CalendarIcon className={`mr-3 h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                  {format(date, "EEEE, MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className={`w-auto p-0 border ${
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`} align="start">
                <Calendar mode="single" selected={date} onSelect={handleDateChange} initialFocus />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleDateChange(addDays(date, 1))}
              className={`h-14 w-14 border flex-shrink-0 ${
                isDark 
                  ? 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 text-slate-400 hover:text-slate-200'
                  : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-600 hover:text-gray-900'
              }`}
              title="Next day"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDateChange(new Date())}
              className={`h-14 px-4 border flex-shrink-0 font-semibold ${
                isDark 
                  ? 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 text-slate-400 hover:text-slate-200'
                  : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-600 hover:text-gray-900'
              }`}
              title="Go to today"
            >
              Today
            </Button>
          </div>
        </div>
        <div className="flex-1">
          <label className={`text-sm uppercase tracking-widest font-semibold mb-2 block ${
            isDark ? 'text-slate-500' : 'text-gray-600'
          }`}>Clinic Location</label>
          <div className="relative" ref={locationInputRef}>
            <Building2 className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 z-10 ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`} />
            <Input
              data-testid="clinic-location-input"
              placeholder="Enter clinic location..."
              value={clinicLocation}
              onChange={(e) => setClinicLocation(e.target.value)}
              onFocus={() => savedLocations.length > 0 && setShowLocationDropdown(true)}
              className={`pl-12 border h-14 text-lg ${
                isDark 
                  ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                  : 'bg-white border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            {/* Saved Locations Dropdown */}
            {showLocationDropdown && savedLocations.length > 0 && (
              <div className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto ${
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`}>
                {savedLocations.map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => handleLocationSelect(loc.name)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b last:border-b-0 ${
                      isDark 
                        ? 'hover:bg-slate-700/50 border-slate-700/50'
                        : 'hover:bg-gray-50 border-gray-100'
                    }`}
                  >
                    <MapPin size={16} className="text-blue-400 flex-shrink-0" />
                    <span className={isDark ? 'text-slate-200' : 'text-gray-900'}>{loc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loadingDate && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-blue-400 border-t-transparent rounded-full" />
        </div>
      )}

      {!loadingDate && (
        <>
          <div className="mb-8">
            <h3 className={`text-2xl md:text-3xl font-bold ${
              isDark ? 'text-blue-300' : 'text-blue-600'
            }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              Quick-Add Visits
            </h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
              Use the + and - buttons to tally patients by CPT code
            </p>
            <p className={`text-xs italic ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>
              Visit types configurable in Settings
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {quickAddVisits.map((cpt, index) => (
              <div key={`${cpt.code}-${index}`} className="animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <TallyCard
                  code={cpt.code}
                  count={tallies[cpt.code] || 0}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  onSetCount={handleSetCount}
                  customVisit={cpt}
                />
              </div>
            ))}
            </div>
          </div>

          <MiscEntries entries={miscEntries} onAdd={handleAddMisc} onRemove={handleRemoveMisc} onUpdate={handleUpdateMisc} onCptListUpdated={() => setCptPanelRefresh(prev => prev + 1)} />

          <ProcedureEntries entries={procedureEntries} onAdd={handleAddProcedure} onRemove={handleRemoveProcedure} onUpdate={handleUpdateProcedure} onCptListUpdated={() => setCptPanelRefresh(prev => prev + 1)} />

          <HourlyEntries entries={hourlyEntries} onAdd={handleAddHourly} onRemove={handleRemoveHourly} onUpdate={handleUpdateHourly} />

          <div className="flex flex-wrap items-end gap-3 mb-8">
            <Button data-testid="save-shift-btn" onClick={handleSave} disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-[0_4px_0_0_rgba(0,0,0,0.3)] active:shadow-none active:translate-y-[4px] transition-all h-10">
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : activeShiftId ? "Update Shift" : "Save Shift"}
            </Button>
            
            {/* CPT Search Tool Section */}
            <div className={`relative flex-1 max-w-2xl h-10 rounded-xl border-2 px-4 shadow-lg ${
              isDark 
                ? 'border-cyan-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/60'
                : 'border-blue-300 bg-white'
            }`} ref={cptSearchRef}>
              <div className="flex items-center gap-4 h-full">
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <Search className={`h-5 w-5 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`} />
                  <span className={`text-lg font-bold tracking-wide whitespace-nowrap ${
                    isDark ? 'text-cyan-300' : 'text-blue-700'
                  }`}>CPT Search Tool</span>
                </div>
                
                <div className="relative flex-1">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${
                    isDark ? 'text-slate-500' : 'text-gray-400'
                  }`} />
                  <Input
                    data-testid="cpt-search-input"
                    type="text"
                    placeholder="Search by code or keywords (e.g., 99213, knee replacement, preventive new infant)..."
                    value={cptSearchQuery}
                    onChange={(e) => handleCptSearch(e.target.value)}
                    onFocus={() => cptSearchResults.length > 0 && setShowCptSearchResults(true)}
                    className={`pl-9 pr-8 h-8 border text-sm rounded-lg ${
                      isDark 
                        ? 'bg-slate-900/70 border-slate-700/50 hover:border-cyan-500/40 focus:border-cyan-500/60 text-slate-200 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-blue-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                    }`}
                  />
                  {cptSearchQuery && (
                    <button
                      onClick={() => { setCptSearchQuery(""); setCptSearchResults([]); setShowCptSearchResults(false); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Search Results Dropdown */}
              {showCptSearchResults && cptSearchResults.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-xl z-[200] max-h-80 overflow-y-auto ${
                  isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                }`}>
                  <div className={`px-3 py-1.5 text-xs border-b flex items-center justify-between ${
                    isDark ? 'text-slate-400 bg-slate-900/60 border-slate-700/50' : 'text-gray-500 bg-gray-50 border-gray-200'
                  }`}>
                    <span>{cptSearchResults.length} result{cptSearchResults.length !== 1 ? 's' : ''} — keywords matched in any order</span>
                    <span className={`${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Left-click → Procedures · Right-click → More options</span>
                  </div>
                  {cptSearchResults.map((cpt, idx) => (
                    <button
                      key={`${cpt.code}-${idx}`}
                      type="button"
                      data-testid={`cpt-result-${cpt.code}`}
                      onClick={() => handleAddCptToProcedureFromSearch(cpt)}
                      onContextMenu={(e) => handleSearchResultContextMenu(e, cpt)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors border-b last:border-b-0 ${
                        isDark 
                          ? 'hover:bg-slate-700/50 border-slate-700/50'
                          : 'hover:bg-blue-50 border-gray-100'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-bold text-sm flex-shrink-0 ${
                            isDark ? 'text-amber-400' : 'text-amber-600'
                          }`}>{cpt.code}</span>
                          <span className={`text-sm truncate ${
                            isDark ? 'text-slate-300' : 'text-gray-700'
                          }`}>{cpt.name}</span>
                        </div>
                      </div>
                      <span className={`font-mono text-sm ml-3 flex-shrink-0 font-semibold ${
                        isDark ? 'text-emerald-400' : 'text-emerald-600'
                      }`}>{cpt.wrvu} wRVU</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div data-testid="daily-summary" className={`rounded-xl border-2 p-6 backdrop-blur-sm ${
            isDark 
              ? 'border-slate-700/30 bg-slate-800/30'
              : 'border-gray-200 bg-white'
          }`}>
            <h3 className={`text-sm uppercase tracking-widest font-semibold mb-4 ${
              isDark ? 'text-slate-500' : 'text-gray-600'
            }`}>Daily Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Date</p>
                <p className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>
                  {format(date, "MM/dd/yyyy")}
                </p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Location</p>
                <p className={`text-sm font-medium truncate ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>
                  {clinicLocation || "—"}
                </p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total Patients</p>
                <p data-testid="summary-patients" className={`text-2xl font-mono font-bold ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`}>{totalPatients.toLocaleString()}</p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total wRVUs</p>
                <p data-testid="summary-wrvus" className={`text-2xl font-mono font-bold ${
                  isDark ? 'text-emerald-400' : 'text-emerald-600'
                }`}>{formatNumber(totalWrvus)}</p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Pre-Tax $</p>
                <p data-testid="summary-pretax" className={`text-2xl font-mono font-bold ${
                  isDark ? 'text-amber-400' : 'text-amber-600'
                }`}>{formatCurrency(preTax)}</p>
              </div>
              <div>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Post-Tax $</p>
                <p data-testid="summary-posttax" className={`text-2xl font-mono font-bold ${
                  isDark ? 'text-sky-400' : 'text-sky-600'
                }`}>{formatCurrency(postTax)}</p>
              </div>
            </div>

            {(totalPatients > 0 || hourlyPay > 0) && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700/30' : 'border-gray-200'}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {quickAddVisits.filter(c => (tallies[c.code] || 0) > 0).map((cpt, idx) => (
                    <div key={`${cpt.code}-${idx}`} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded-md ${
                      isDark ? 'bg-slate-700/20' : 'bg-gray-100'
                    }`}>
                      <span className={`font-mono ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{cpt.label}</span>
                      <span className={`font-mono font-semibold ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>
                        {tallies[cpt.code]} x {cpt.wrvu}
                      </span>
                    </div>
                  ))}
                  {miscEntries.filter(e => (parseFloat(e.wrvus) || 0) > 0).map((entry, i) => (
                    <div key={entry.id} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded-md ${
                      isDark ? 'bg-purple-500/10' : 'bg-purple-50'
                    }`}>
                      <span className={`font-mono truncate mr-2 ${
                        isDark ? 'text-purple-400' : 'text-purple-700'
                      }`}>{entry.label || `Misc #${i + 1}`}</span>
                      <span className={`font-mono font-semibold ${
                        isDark ? 'text-purple-300' : 'text-purple-800'
                      }`}>{formatNumber(parseFloat(entry.wrvus) || 0)}</span>
                    </div>
                  ))}
                  {procedureEntries.filter(e => (parseFloat(e.wrvus) || 0) > 0).map((entry, i) => (
                    <div key={entry.id} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded-md ${
                      isDark ? 'bg-teal-500/10' : 'bg-teal-50'
                    }`}>
                      <span className={`font-mono truncate mr-2 ${
                        isDark ? 'text-teal-400' : 'text-teal-700'
                      }`}>{entry.label || `Proc #${i + 1}`}</span>
                      <span className={`font-mono font-semibold ${
                        isDark ? 'text-teal-300' : 'text-teal-800'
                      }`}>{formatNumber(parseFloat(entry.wrvus) || 0)}</span>
                    </div>
                  ))}
                  {hourlyEntries.filter(e => (parseFloat(e.rate) || 0) > 0 && (parseFloat(e.hours) || 0) > 0).map((entry, i) => (
                    <div key={entry.id} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded-md ${
                      isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'
                    }`}>
                      <span className={`font-mono truncate mr-2 ${
                        isDark ? 'text-cyan-400' : 'text-cyan-700'
                      }`}>{entry.label || `Hourly #${i + 1}`}</span>
                      <span className={`font-mono font-semibold ${
                        isDark ? 'text-cyan-300' : 'text-cyan-800'
                      }`}>{formatCurrency((parseFloat(entry.rate) || 0) * (parseFloat(entry.hours) || 0))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Totals Bar - Sticky until it reaches its natural position */}
      <div className="sticky bottom-0 z-50 mt-8">
        <TotalsBar totalPatients={totalPatients} totalWrvus={totalWrvus} hourlyPay={hourlyPay} retentionRate={retentionRate} wrvuRate={wrvuRate} />
      </div>
      
      {/* CPT Search Context Menu */}
      {searchContextMenu && (
        <CptSearchContextMenu
          x={searchContextMenu.x}
          y={searchContextMenu.y}
          cpt={searchContextMenu.cpt}
          onClose={() => setSearchContextMenu(null)}
          onAddToMisc={handleAddCptToMiscFromSearch}
          onAddToProcedure={handleAddCptToProcedureFromSearch}
          onAddToQuickAdd={handleAddCptToQuickAddFromSearch}
        />
      )}
    </div>
  );
}
